import { createSSEParser, type StreamEvent } from "./sse";
import { forceRefresh, getAccessToken, reportSignedOut, reportSuccess } from "./session";
import { appFetch, isDesktopApp, resolveApiUrl } from "./runtime";

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  keepalive?: boolean;
}

function resolvePath(endpoint: string) {
  return resolveApiUrl(endpoint);
}

/** Build the error the non-streaming and streaming paths both throw. */
async function errorFromResponse(response: Response) {
  let errorMessage = `API error: ${response.status}`;
  let body: unknown;
  try {
    body = await response.json();
    const message = (body as { message?: string })?.message;
    if (message) errorMessage = message;
  } catch {
    // ignore
  }
  const error = new Error(errorMessage) as Error & {
    status?: number;
    body?: unknown;
  };
  error.status = response.status;
  // Kept so callers can act on the payload — a 409 carries the server's copy,
  // which is what distinguishes a real conflict from one with our own write.
  error.body = body;
  return error;
}

async function sendToServer<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const path = resolvePath(endpoint);

  const send = async (token: string | null) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...options.headers,
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return appFetch(path, {
      method: options.method || "GET",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      keepalive: options.keepalive,
    });
  };

  let response: Response;
  if (options.skipAuth) {
    response = await send(null);
  } else {
    response = await send(await getAccessToken());

    // One retry on 401. The token can go stale between the expiry check and
    // the server reading it — clock skew, a rotation elsewhere, a request
    // queued behind a slow one. Refreshing and retrying once turns that into
    // a hiccup instead of a silent failure the user only notices when their
    // writing doesn't save.
    if (response.status === 401) {
      const fresh = await forceRefresh();
      if (fresh) {
        response = await send(fresh);
      }
      // Only a failed refresh means the session is gone. A fresh token
      // that still 401s is the endpoint — Suggest used to leak Anthropic's
      // 401 through, and treating that as signed-out stuck the whole Mac
      // app on "your session expired" for the rest of the run.
      if (response.status === 401 && !fresh) {
        reportSignedOut();
      }
    }
  }

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  reportSuccess();

  if (response.status === 204) {
    return {} as T;
  }

  return (await response.json()) as T;
}

export async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const path = resolvePath(endpoint);
  const method = (options.method || "GET").toUpperCase();

  // The Mac library is served from disk when the hop fails. The website has
  // no local copy, so it keeps talking to the server only.
  if (isDesktopApp && !options.skipAuth) {
    const { desktopGet, desktopMutate } = await import("./desktop-sync");
    const run = () => sendToServer<T>(endpoint, options);
    if (method === "GET") return desktopGet(path, run);
    return desktopMutate(path, method, run, {
      method,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  }

  return sendToServer<T>(endpoint, options);
}

export const get = <T>(endpoint: string, options?: ApiOptions) =>
  api<T>(endpoint, { method: "GET", ...options });

export const post = <T>(endpoint: string, data: unknown, options?: ApiOptions) =>
  api<T>(endpoint, { method: "POST", body: data, ...options });

export const put = <T>(endpoint: string, data: unknown, options?: ApiOptions) =>
  api<T>(endpoint, { method: "PUT", body: data, ...options });

export const del = <T>(endpoint: string, options?: ApiOptions) =>
  api<T>(endpoint, { method: "DELETE", ...options });

/* ------------------------------------------------------------------ *
 * Streaming
 *
 * `api()` above always buffers the whole body via response.json(), which is
 * fine for CRUD but not for model output — the point there is showing tokens
 * as they arrive. These helpers read the body incrementally instead.
 * ------------------------------------------------------------------ */

export interface StreamOptions extends Omit<ApiOptions, "method"> {
  method?: string;
  /** Abort the request — pass this so navigating away cancels generation. */
  signal?: AbortSignal;
}

export type { StreamEvent };

/**
 * POST to `endpoint` and yield Server-Sent Events as they arrive.
 *
 * Deliberately untyped at the payload level: it parses the SSE envelope only,
 * so it works whether the route proxies an upstream stream verbatim or emits
 * its own event names.
 */
export async function* streamEvents(
  endpoint: string,
  options: StreamOptions = {},
): AsyncGenerator<StreamEvent, void, undefined> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    ...options.headers,
  };

  if (!options.skipAuth) {
    const token = await getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response = await appFetch(resolvePath(endpoint), {
    method: options.method || "POST",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (response.status === 401 && !options.skipAuth) {
    const fresh = await forceRefresh();
    if (fresh) {
      headers.Authorization = `Bearer ${fresh}`;
      response = await appFetch(resolvePath(endpoint), {
        method: options.method || "POST",
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        signal: options.signal,
      });
    }
    if (response.status === 401 && !fresh) reportSignedOut();
  }

  if (!response.ok) throw await errorFromResponse(response);
  reportSuccess();
  if (!response.body) throw new Error("Streaming is not supported in this environment");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSSEParser();

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const event of parser.push(decoder.decode(value, { stream: true }))) {
        yield event;
      }
    }
    // A final frame that arrived without a terminating blank line still counts.
    for (const event of parser.flush()) yield event;
  } finally {
    // Aborting mid-stream would otherwise leave the body open, holding the
    // connection until the server gives up.
    reader.cancel().catch(() => undefined);
  }
}

/**
 * Yield assistant text deltas from a route that proxies an Anthropic message
 * stream, so callers can append strings without knowing the event schema.
 *
 * Thinking deltas are skipped — they are reasoning, not document content.
 * Terminates on the `[DONE]` sentinel if the route sends one; raises whatever
 * an `error` event carries.
 */
export async function* streamText(
  endpoint: string,
  options: StreamOptions = {},
): AsyncGenerator<string, void, undefined> {
  for await (const { event, data } of streamEvents(endpoint, options)) {
    if (data === "[DONE]") return;

    let payload: {
      type?: string;
      delta?: { type?: string; text?: string };
      error?: { message?: string };
      message?: string;
    };
    try {
      payload = JSON.parse(data);
    } catch {
      continue; // not JSON — nothing meaningful to surface
    }

    const type = payload.type ?? event;

    if (type === "error") {
      throw new Error(payload.error?.message || payload.message || "Stream error");
    }

    if (type === "content_block_delta" && payload.delta?.type === "text_delta") {
      if (payload.delta.text) yield payload.delta.text;
    }

    if (type === "message_stop") return;
  }
}
